import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Row, Table, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FaCashRegister, FaEdit, FaFileInvoiceDollar, FaPrint, FaShoppingBag, FaTrash } from "react-icons/fa";
import {
  actualizarComprobanteVenta,
  actualizarOrdenVenta,
  eliminarComprobanteVenta,
  eliminarOrdenVenta,
  obtenerClientesDesdeCompany,
  obtenerComprobantesVentaPorEmpresa,
  obtenerOrdenesVentaPorEmpresa,
  registrarComprobanteVenta,
  registrarOrdenVenta,
} from "../../services/contabilidad/ventasService";
import { generarPdfOrdenVenta } from "../../services/contabilidad/documentosPdfService";
import { checkPermission } from "../../functions/checkPermission";
import { logBusinessAction } from "../../services/AuditService";

const HEADER_STYLE = {
  background: "linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)",
  color: "white",
  borderRadius: "15px",
  padding: "2rem",
};

const ORDEN_INICIAL = {
  fecha: new Date().toISOString().slice(0, 10),
  clienteRuc: "",
  clienteRazonSocial: "",
  descripcion: "",
  moneda: "PEN",
  total: "",
  lineas: [
    { descripcion: "", cantidad: 1, precioUnitario: "" },
  ],
};

const COMPROBANTE_INICIAL = {
  ordenVentaId: "",
  clienteRuc: "",
  clienteRazonSocial: "",
  tipo: "FACTURA",
  serie: "F001",
  numero: "",
  fechaEmision: new Date().toISOString().slice(0, 10),
  fechaVencimiento: new Date().toISOString().slice(0, 10),
  fechaCorte: "",
  periodoContable: new Date().toISOString().slice(0, 7),
  moneda: "PEN",
  subtotal: "",
  igv: "",
  total: "",
  lineas: [
    { descripcion: "", cantidad: 1, precioUnitario: "" },
  ],
};

const fmt = (x) => Number(x || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const toNum = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

const GestionVentasModulo = ({ usuario, seccionInicial = "ordenes", mostrarTabsRuta = true, baseRuta = "/gestion-ventas", modoContabilidad = false }) => {
  const { seccion: urlSeccion } = useParams();
  const navigate = useNavigate();
  const permiteComprobantes = Boolean(modoContabilidad);
  const [seccionInterna, setSeccionInterna] = useState(seccionInicial === "comprobantes" ? "comprobantes" : "ordenes");
  const seccion = mostrarTabsRuta
    ? (["ordenes", "comprobantes"].includes(urlSeccion) ? urlSeccion : "ordenes")
    : seccionInterna;
  const rucEmpresa = usuario?.ruc || usuario?.rucEmpresa || "";
  const tienePermisoVentas = (proceso) =>
    checkPermission(usuario, "ventas") ||
    checkPermission(usuario, "ventas", proceso) ||
    checkPermission(usuario, "contabilidad") ||
    checkPermission(usuario, "contabilidad", "VENTAS");
  const puedeVerOrdenes = tienePermisoVentas("ORDENES");
  const puedeVerComprobantes = permiteComprobantes && tienePermisoVentas("COMPROBANTES");

  const [ordenes, setOrdenes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [comprobantes, setComprobantes] = useState([]);
  const [ordenForm, setOrdenForm] = useState(ORDEN_INICIAL);
  const [comprobanteForm, setComprobanteForm] = useState(COMPROBANTE_INICIAL);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");

  const cargar = async () => {
    if (!rucEmpresa) return;
    setLoading(true);
    setError("");
    try {
      const [ov, cv, clientesData] = await Promise.all([
        obtenerOrdenesVentaPorEmpresa(rucEmpresa),
        obtenerComprobantesVentaPorEmpresa(rucEmpresa),
        obtenerClientesDesdeCompany({
          rucEmpresa,
          empresasHabilitadas: usuario?.empresasHabilitadas || [],
        }),
      ]);
      setOrdenes(ov);
      setComprobantes(cv);
      setClientes(clientesData);
    } catch (e) {
      console.error(e);
      setError("No se pudieron cargar los datos de ventas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [rucEmpresa, usuario?.empresasHabilitadas]);

  useEffect(() => {
    if (!mostrarTabsRuta) {
      setSeccionInterna(seccionInicial === "comprobantes" ? "comprobantes" : "ordenes");
    }
  }, [seccionInicial, mostrarTabsRuta]);

  useEffect(() => {
    if (!permiteComprobantes && seccion === "comprobantes") {
      if (mostrarTabsRuta) {
        navigate(`${baseRuta}/ordenes`, { replace: true });
      } else {
        setSeccionInterna("ordenes");
      }
      return;
    }

    const sinPermisoOrdenes = seccion === "ordenes" && !puedeVerOrdenes;
    const sinPermisoComprobantes = seccion === "comprobantes" && !puedeVerComprobantes;
    if (!sinPermisoOrdenes && !sinPermisoComprobantes) return;

    if (puedeVerOrdenes) {
      if (mostrarTabsRuta) {
        navigate(`${baseRuta}/ordenes`, { replace: true });
      } else {
        setSeccionInterna("ordenes");
      }
      return;
    }

    if (puedeVerComprobantes) {
      if (mostrarTabsRuta) {
        navigate(`${baseRuta}/comprobantes`, { replace: true });
      } else {
        setSeccionInterna("comprobantes");
      }
    }
  }, [permiteComprobantes, seccion, mostrarTabsRuta, navigate, baseRuta, puedeVerOrdenes, puedeVerComprobantes]);

  const resumen = useMemo(() => {
    const totalOV = ordenes.reduce((acc, x) => acc + Number(x.total || 0), 0);
    const totalFacturado = comprobantes.reduce((acc, x) => acc + Number(x.total || 0), 0);
    return { totalOV, totalFacturado };
  }, [ordenes, comprobantes]);

  const ordenesDisponiblesFacturar = useMemo(() => {
    return ordenes.filter((o) => {
      const total = toNum(o.total);
      const totalFacturado = toNum(o.total_facturado);
      const saldo = toNum(o.saldo_pendiente != null ? o.saldo_pendiente : (total - totalFacturado));
      return saldo > 0.01;
    });
  }, [ordenes]);

  const onOrdenChange = (e) => setOrdenForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const onLineaOrdenChange = (index, field, value) => {
    setOrdenForm((prev) => {
      const lineas = [...(prev.lineas || [])];
      lineas[index] = { ...lineas[index], [field]: value };
      return { ...prev, lineas };
    });
  };
  const addLineaOrden = () => {
    setOrdenForm((prev) => ({
      ...prev,
      lineas: [...(prev.lineas || []), { descripcion: "", cantidad: 1, precioUnitario: "" }],
    }));
  };
  const removeLineaOrden = (index) => {
    setOrdenForm((prev) => {
      const lineas = [...(prev.lineas || [])];
      lineas.splice(index, 1);
      return { ...prev, lineas: lineas.length > 0 ? lineas : [{ descripcion: "", cantidad: 1, precioUnitario: "" }] };
    });
  };

  const totalOrdenCalculado = useMemo(() => {
    return (ordenForm.lineas || []).reduce((acc, l) => acc + (toNum(l.cantidad) * toNum(l.precioUnitario)), 0);
  }, [ordenForm.lineas]);
  const onClienteOrdenChange = (e) => {
    const rucCliente = e.target.value;
    const cliente = clientes.find((c) => c.ruc === rucCliente);
    if (!cliente) {
      setOrdenForm((p) => ({ ...p, clienteRuc: "", clienteRazonSocial: "" }));
      return;
    }
    setOrdenForm((p) => ({
      ...p,
      clienteRuc: cliente.ruc,
      clienteRazonSocial: cliente.razon_social,
    }));
  };
  const onCompChange = (e) => {
    const { name, value } = e.target;
    if (name === "ordenVentaId") {
      const ov = ordenes.find((o) => o.id === value);
      if (ov) {
        const lineasOV = Array.isArray(ov.lineas)
          ? ov.lineas.map((l, idx) => ({
              descripcion: l.descripcion || "",
              lineaOsId: `${ov.id}_${idx}`,
              indiceOs: idx,
              cantidad: l.cantidad || 1,
              precioUnitario: l.precio_unitario ?? l.precioUnitario ?? "",
            }))
          : [{ descripcion: "", cantidad: 1, precioUnitario: "" }];

        setComprobanteForm((p) => ({
          ...p,
          ordenVentaId: value,
          clienteRuc: ov.cliente_ruc || "",
          clienteRazonSocial: ov.cliente_razon_social || "",
          lineas: lineasOV,
        }));
        return;
      }
    }
    setComprobanteForm((p) => ({ ...p, [name]: value }));
  };

  const onLineaCompChange = (index, field, value) => {
    setComprobanteForm((prev) => {
      const lineas = [...(prev.lineas || [])];
      lineas[index] = { ...lineas[index], [field]: value };
      return { ...prev, lineas };
    });
  };
  const addLineaComp = () => {
    setComprobanteForm((prev) => ({
      ...prev,
      lineas: [...(prev.lineas || []), { descripcion: "", cantidad: 1, precioUnitario: "" }],
    }));
  };
  const removeLineaComp = (index) => {
    setComprobanteForm((prev) => {
      const lineas = [...(prev.lineas || [])];
      lineas.splice(index, 1);
      return { ...prev, lineas: lineas.length > 0 ? lineas : [{ descripcion: "", cantidad: 1, precioUnitario: "" }] };
    });
  };

  const subtotalComprobanteCalculado = useMemo(() => {
    return (comprobanteForm.lineas || []).reduce((acc, l) => acc + (toNum(l.cantidad) * toNum(l.precioUnitario)), 0);
  }, [comprobanteForm.lineas]);
  const igvComprobanteCalculado = useMemo(() => Number((subtotalComprobanteCalculado * 0.18).toFixed(2)), [subtotalComprobanteCalculado]);
  const totalComprobanteCalculado = useMemo(() => Number((subtotalComprobanteCalculado + igvComprobanteCalculado).toFixed(2)), [subtotalComprobanteCalculado, igvComprobanteCalculado]);

  const guardarOrden = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setOk("");
    try {
      const lineasValidas = (ordenForm.lineas || [])
        .map((l) => ({
          descripcion: String(l.descripcion || "").trim(),
          cantidad: toNum(l.cantidad),
          precioUnitario: toNum(l.precioUnitario),
          importe: Number((toNum(l.cantidad) * toNum(l.precioUnitario)).toFixed(2)),
        }))
        .filter((l) => l.descripcion && l.cantidad > 0 && l.precioUnitario > 0);

      if (lineasValidas.length === 0) {
        throw new Error("Debe agregar al menos una línea válida en la orden de venta.");
      }

      if (editandoId) {
        await actualizarOrdenVenta({
          rucEmpresa,
          ordenId: editandoId,
          orden: { ...ordenForm, lineas: lineasValidas },
          usuarioRegistro: usuario?.correo || usuario?.dni || "sistema",
        });

        // AUDITORÍA EDICIÓN
        try {
          await logBusinessAction("EDICION_ORDEN_VENTA", "GestionVentas", {
            ordenId: editandoId,
            cliente: ordenForm.clienteRazonSocial,
            total: Number(totalOrdenCalculado.toFixed(2))
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de edición de orden de venta", e);
        }

        setOk("Orden de venta actualizada correctamente.");
      } else {
        await registrarOrdenVenta({
          rucEmpresa,
          orden: {
            ...ordenForm,
            lineas: lineasValidas,
            total: Number(totalOrdenCalculado.toFixed(2)),
          },
          usuarioRegistro: usuario?.correo || usuario?.dni || "sistema",
        });

        // AUDITORÍA CREACIÓN
        try {
          await logBusinessAction("CREACION_ORDEN_VENTA", "GestionVentas", {
            cliente: ordenForm.clienteRazonSocial,
            total: Number(totalOrdenCalculado.toFixed(2))
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de creación de orden de venta", e);
        }

        setOk("Orden de venta registrada y amarrada a documento comercial.");
      }
      setOrdenForm(ORDEN_INICIAL);
      setEditandoId(null);
      await cargar();
    } catch (e) {
      setError(e?.message || "No se pudo procesar la orden de venta.");
    } finally {
      setSaving(false);
    }
  };

  const guardarComprobante = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setOk("");
    try {
      const lineasValidas = (comprobanteForm.lineas || [])
        .map((l) => ({
          descripcion: String(l.descripcion || "").trim(),
          lineaOsId: l.lineaOsId || "",
          indiceOs: Number.isFinite(Number(l.indiceOs)) ? Number(l.indiceOs) : null,
          cantidad: toNum(l.cantidad),
          precioUnitario: toNum(l.precioUnitario),
          importe: Number((toNum(l.cantidad) * toNum(l.precioUnitario)).toFixed(2)),
        }))
        .filter((l) => l.descripcion && l.cantidad > 0 && l.precioUnitario > 0);

      if (lineasValidas.length === 0) {
        throw new Error("Debe agregar al menos una línea válida en la factura/boleta.");
      }

      if (editandoId) {
        await actualizarComprobanteVenta({
            rucEmpresa,
            comprobanteId: editandoId,
            comprobante: { ...comprobanteForm, lineas: lineasValidas },
            usuarioRegistro: usuario?.correo || usuario?.dni || "sistema",
        });

        // AUDITORÍA EDICIÓN
        try {
          await logBusinessAction("EDICION_COMPROBANTE_VENTA", "GestionVentas", {
            comprobanteId: editandoId,
            cliente: comprobanteForm.clienteRazonSocial,
            total: totalComprobanteCalculado,
            tipo: comprobanteForm.tipo,
            serie: comprobanteForm.serie,
            numero: comprobanteForm.numero
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de edición de comprobante de venta", e);
        }

        setOk("Comprobante actualizado correctamente.");
      } else {
        await registrarComprobanteVenta({
            rucEmpresa,
            comprobante: {
              ...comprobanteForm,
              lineas: lineasValidas,
              subtotal: subtotalComprobanteCalculado,
              igv: igvComprobanteCalculado,
              total: totalComprobanteCalculado,
            },
            usuarioRegistro: usuario?.correo || usuario?.dni || "sistema",
          });

          // AUDITORÍA CREACIÓN
          try {
            await logBusinessAction("CREACION_COMPROBANTE_VENTA", "GestionVentas", {
              cliente: comprobanteForm.clienteRazonSocial,
              total: totalComprobanteCalculado,
              tipo: comprobanteForm.tipo,
              serie: comprobanteForm.serie,
              numero: comprobanteForm.numero
            }, usuario);
          } catch (e) {
            console.warn("No se pudo registrar la auditoría de creación de comprobante de venta", e);
          }

          setOk("Comprobante de venta registrado con documento y asiento contable.");
      }
      setComprobanteForm(COMPROBANTE_INICIAL);
      setEditandoId(null);
      await cargar();
    } catch (e) {
      setError(e?.message || "No se pudo procesar el comprobante de venta.");
    } finally {
      setSaving(false);
    }
  };

  const handleEditOrden = (o) => {
    setEditandoId(o.id);
    setOrdenForm({
        fecha: o.fecha || "",
        clienteRuc: o.cliente_ruc || "",
        clienteRazonSocial: o.cliente_razon_social || "",
        descripcion: o.descripcion || "",
        moneda: o.moneda || "PEN",
        lineas: (o.lineas || []).map(l => ({
            descripcion: l.descripcion || "",
            cantidad: l.cantidad || 1,
            precioUnitario: l.precio_unitario || ""
        }))
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditComprobante = (c) => {
    setEditandoId(c.id);
    setComprobanteForm({
        ordenVentaId: c.orden_venta_id || "",
        clienteRuc: c.cliente_ruc || "",
        clienteRazonSocial: c.cliente_razon_social || "",
        tipo: c.tipo || "FACTURA",
        serie: c.serie || "",
        numero: c.numero || "",
        fechaEmision: c.fecha_emision || "",
        fechaVencimiento: c.fecha_vencimiento || "",
        fechaCorte: c.fecha_corte || "",
        periodoContable: c.periodo_contable || "",
        moneda: c.moneda || "PEN",
        lineas: (c.lineas || []).map(l => ({
            descripcion: l.descripcion || "",
            lineaOsId: l.linea_os_id || "",
            indiceOs: l.indice_os ?? null,
            cantidad: l.cantidad || 1,
            precioUnitario: l.precio_unitario || ""
        }))
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteOrden = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar esta Orden de Venta? Solo se podrá eliminar si no tiene facturas asociadas.")) return;
    setLoading(true);
    try {
        await eliminarOrdenVenta({ rucEmpresa, ordenVentaId: id });

        // AUDITORÍA ELIMINACIÓN
        try {
          await logBusinessAction("ELIMINACION_ORDEN_VENTA", "GestionVentas", {
            ordenVentaId: id
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de eliminación de orden de venta", e);
        }

        setOk("Orden de venta eliminada.");
        await cargar();
    } catch (e) {
        setError(e.message);
    } finally {
        setLoading(false);
    }
  };

  const handleDeleteComprobante = async (id) => {
    if (!window.confirm("¿Está seguro de eliminar este Comprobante? Esto revertirá el saldo en la OS y borrará el asiento contable.")) return;
    setLoading(true);
    try {
        await eliminarComprobanteVenta({ rucEmpresa, comprobanteId: id });

        // AUDITORÍA ELIMINACIÓN
        try {
          await logBusinessAction("ELIMINACION_COMPROBANTE_VENTA", "GestionVentas", {
            comprobanteId: id
          }, usuario);
        } catch (e) {
          console.warn("No se pudo registrar la auditoría de eliminación de comprobante de venta", e);
        }

        setOk("Comprobante eliminado y saldos revertidos.");
        await cargar();
    } catch (e) {
        setError(e.message);
    } finally {
        setLoading(false);
    }
  };

  const imprimirOV = (ov) => {
    try {
      generarPdfOrdenVenta({ ov, rucEmpresa });
    } catch (e) {
      setError(e?.message || "No se pudo generar el PDF de la orden de venta.");
    }
  };

  const clienteBloqueadoEnComprobante = Boolean(comprobanteForm.ordenVentaId);

  return (
    <Container fluid className="p-4 animate__animated animate__fadeIn">
      <div style={HEADER_STYLE} className="shadow-sm mb-4">
        <div className="d-flex align-items-center mb-0">
          <div className="bg-white bg-opacity-20 p-3 rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: "60px", height: "60px" }}>
            <FaShoppingBag size={26} />
          </div>
          <div className="flex-grow-1">
            <h2 className="fw-bold mb-1">Gestión de Ventas</h2>
            <p className="mb-0 opacity-75 small">Gestión comercial de órdenes de venta y trazabilidad con contabilidad.</p>
          </div>
          <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill shadow-sm">Empresa: {rucEmpresa || "No definida"}</Badge>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {ok && <Alert variant="success">{ok}</Alert>}
      {!puedeVerOrdenes && !puedeVerComprobantes && (
        <Alert variant="danger" className="border-0 shadow-sm p-4">
          <h5 className="fw-bold">Acceso Denegado</h5>
          No tiene permisos granulares para acceder a Gestión de Ventas.
        </Alert>
      )}
      {(puedeVerOrdenes || puedeVerComprobantes) && (
        <>
      {!permiteComprobantes && (
        <Alert variant="info">
          El registro de facturas/boletas se realiza en Contabilidad. Los cobros/pagos se registran en Tesorería.
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="border-0 shadow-sm"><Card.Body><small className="text-muted">Total Órdenes de Venta</small><h4 className="mb-0">S/ {fmt(resumen.totalOV)}</h4></Card.Body></Card>
        </Col>
        <Col md={6}>
          <Card className="border-0 shadow-sm"><Card.Body><small className="text-muted">Total Facturado</small><h4 className="mb-0">S/ {fmt(resumen.totalFacturado)}</h4></Card.Body></Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
          <div className="d-flex gap-2">
            {puedeVerOrdenes && (
                <Button
                variant={seccion === "ordenes" ? "primary" : "outline-primary"}
                onClick={() => {
                    setEditandoId(null);
                    setOrdenForm(ORDEN_INICIAL);
                    mostrarTabsRuta ? navigate(`${baseRuta}/ordenes`) : setSeccionInterna("ordenes");
                }}
                >
                <FaCashRegister className="me-1" /> Órdenes de Venta
                </Button>
            )}
            {puedeVerComprobantes && (
                <Button
                variant={seccion === "comprobantes" ? "success" : "outline-success"}
                onClick={() => {
                    setEditandoId(null);
                    setComprobanteForm(COMPROBANTE_INICIAL);
                    mostrarTabsRuta ? navigate(`${baseRuta}/comprobantes`) : setSeccionInterna("comprobantes");
                }}
                >
                <FaFileInvoiceDollar className="me-1" /> Facturas / Boletas
                </Button>
            )}
          </div>
          {editandoId && (
            <Badge bg="warning" text="dark" className="p-2">
                MODO EDICIÓN ACTIVO (ID: {editandoId.slice(0,8)})
                <Button size="sm" variant="link" className="text-dark p-0 ms-2" onClick={() => {
                    setEditandoId(null);
                    setOrdenForm(ORDEN_INICIAL);
                    setComprobanteForm(COMPROBANTE_INICIAL);
                }}>Cancelar</Button>
            </Badge>
          )}
        </Card.Header>
        <Card.Body>
          {seccion === "ordenes" ? (
            <Form onSubmit={guardarOrden}>
              <Row className="g-3">
                <Col md={3}><Form.Label>Fecha</Form.Label><Form.Control type="date" name="fecha" value={ordenForm.fecha} onChange={onOrdenChange} required /></Col>
                <Col md={9}>
                  <Form.Label>Cliente (colección company)</Form.Label>
                  <Form.Select value={ordenForm.clienteRuc} onChange={onClienteOrdenChange} required>
                    <option value="">Seleccionar cliente...</option>
                    {clientes.map((c) => (
                      <option key={c.ruc} value={c.ruc}>
                        {c.razon_social} ({c.ruc})
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={8}><Form.Label>Descripción General</Form.Label><Form.Control name="descripcion" value={ordenForm.descripcion} onChange={onOrdenChange} placeholder="Detalle general de la OV" /></Col>
                <Col md={4}><Form.Label>Moneda</Form.Label><Form.Select name="moneda" value={ordenForm.moneda} onChange={onOrdenChange}><option value="PEN">PEN</option><option value="USD">USD</option></Form.Select></Col>

                <Col md={12}>
                  <div className="border rounded-3 p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Líneas de la OV</strong>
                      <Button size="sm" variant="outline-primary" onClick={addLineaOrden}>+ Agregar línea</Button>
                    </div>
                    {(ordenForm.lineas || []).map((linea, idx) => (
                      <Row className="g-2 mb-2" key={`linea-${idx}`}>
                        <Col md={6}>
                          <Form.Control
                            placeholder="Ej. Servicio de control de asistencia"
                            value={linea.descripcion}
                            onChange={(e) => onLineaOrdenChange(idx, "descripcion", e.target.value)}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="number"
                            min="1"
                            step="1"
                            placeholder="Cant."
                            value={linea.cantidad}
                            onChange={(e) => onLineaOrdenChange(idx, "cantidad", e.target.value)}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="P.Unit"
                            value={linea.precioUnitario}
                            onChange={(e) => onLineaOrdenChange(idx, "precioUnitario", e.target.value)}
                          />
                        </Col>
                        <Col md={1} className="d-flex align-items-center justify-content-end fw-semibold">
                          {fmt(toNum(linea.cantidad) * toNum(linea.precioUnitario))}
                        </Col>
                        <Col md={1}>
                          <Button size="sm" variant="outline-danger" onClick={() => removeLineaOrden(idx)}>✕</Button>
                        </Col>
                      </Row>
                    ))}
                  </div>
                </Col>

                <Col md={12} className="text-end">
                  <div className="fw-bold">Total OV: {ordenForm.moneda} {fmt(totalOrdenCalculado)}</div>
                </Col>
              </Row>
               <div className="d-flex justify-content-end mt-3 gap-2">
                  {editandoId && <Button variant="secondary" onClick={() => { setEditandoId(null); setOrdenForm(ORDEN_INICIAL); }}>Cancelar Edición</Button>}
                  <Button type="submit" disabled={saving || !rucEmpresa}>{saving ? "Guardando..." : (editandoId ? "Actualizar Orden" : "Registrar Orden")}</Button>
               </div>
            </Form>
          ) : (
            <Form onSubmit={guardarComprobante}>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Orden de Venta</Form.Label>
                  <Form.Select name="ordenVentaId" value={comprobanteForm.ordenVentaId} onChange={onCompChange}>
                    <option value="">Sin orden</option>
                    {ordenesDisponiblesFacturar.map((o) => {
                      const saldo = toNum(o.saldo_pendiente != null ? o.saldo_pendiente : (toNum(o.total) - toNum(o.total_facturado)));
                      return (
                        <option key={o.id} value={o.id}>
                          {o.correlativo || o.id.slice(0, 8)} - {o.cliente_razon_social} ({o.cliente_ruc || "-"}) · Saldo: {o.moneda || "PEN"} {fmt(saldo)}
                        </option>
                      );
                    })}
                  </Form.Select>
                  {clienteBloqueadoEnComprobante && (
                    <small className="text-primary d-block mt-1">
                      Cliente heredado de la OV seleccionada.
                    </small>
                  )}
                </Col>
                <Col md={4}>
                  <Form.Label>Cliente RUC</Form.Label>
                  <Form.Control
                    name="clienteRuc"
                    value={comprobanteForm.clienteRuc}
                    onChange={onCompChange}
                    required
                    disabled={clienteBloqueadoEnComprobante}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label>Cliente Razón Social</Form.Label>
                  <Form.Control
                    name="clienteRazonSocial"
                    value={comprobanteForm.clienteRazonSocial}
                    onChange={onCompChange}
                    required
                    disabled={clienteBloqueadoEnComprobante}
                  />
                </Col>
                <Col md={2}><Form.Label>Tipo</Form.Label><Form.Select name="tipo" value={comprobanteForm.tipo} onChange={onCompChange}><option value="FACTURA">FACTURA</option><option value="BOLETA">BOLETA</option></Form.Select></Col>
                <Col md={2}><Form.Label>Serie</Form.Label><Form.Control name="serie" value={comprobanteForm.serie} onChange={onCompChange} required /></Col>
                <Col md={2}><Form.Label>Número</Form.Label><Form.Control name="numero" value={comprobanteForm.numero} onChange={onCompChange} required /></Col>
                <Col md={2}><Form.Label>Moneda</Form.Label><Form.Select name="moneda" value={comprobanteForm.moneda} onChange={onCompChange}><option value="PEN">PEN</option><option value="USD">USD</option></Form.Select></Col>
                
                <Col md={4}><Form.Label>Fecha Emisión</Form.Label><Form.Control type="date" name="fechaEmision" value={comprobanteForm.fechaEmision} onChange={onCompChange} required /></Col>
                <Col md={4}><Form.Label>Fecha Vencimiento</Form.Label><Form.Control type="date" name="fechaVencimiento" value={comprobanteForm.fechaVencimiento} onChange={onCompChange} required /></Col>
                <Col md={4}>
                  <Form.Label>Periodo Contable (Mes de Servicio)</Form.Label>
                  <Form.Control type="month" name="periodoContable" value={comprobanteForm.periodoContable || ""} onChange={onCompChange} required />
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>Indica a qué mes corresponde este servicio facturado.</small>
                </Col>
                <Col md={4}>
                    <Form.Label>Fecha Corte (Bloqueo)</Form.Label>
                    <Form.Control type="date" name="fechaCorte" value={comprobanteForm.fechaCorte} onChange={onCompChange} />
                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>Si la factura vence y pasa esta fecha, se bloqueará el acceso.</small>
                </Col>

                <Col md={12}>
                  <div className="border rounded-3 p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Líneas de Factura/Boleta</strong>
                      <Button size="sm" variant="outline-primary" onClick={addLineaComp}>+ Agregar línea</Button>
                    </div>
                    {(comprobanteForm.lineas || []).map((linea, idx) => (
                      <Row className="g-2 mb-2" key={`linea-comp-${idx}`}>
                        <Col md={6}>
                          <Form.Control
                            placeholder="Ej. Servicio de firma digital"
                            value={linea.descripcion}
                            onChange={(e) => onLineaCompChange(idx, "descripcion", e.target.value)}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="number"
                            min="1"
                            step="1"
                            placeholder="Cant."
                            value={linea.cantidad}
                            onChange={(e) => onLineaCompChange(idx, "cantidad", e.target.value)}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control
                            type="number"
                            min="0.01"
                            step="0.01"
                            placeholder="P.Unit"
                            value={linea.precioUnitario}
                            onChange={(e) => onLineaCompChange(idx, "precioUnitario", e.target.value)}
                          />
                        </Col>
                        <Col md={1} className="d-flex align-items-center justify-content-end fw-semibold">
                          {fmt(toNum(linea.cantidad) * toNum(linea.precioUnitario))}
                        </Col>
                        <Col md={1}>
                          <Button size="sm" variant="outline-danger" onClick={() => removeLineaComp(idx)}>✕</Button>
                        </Col>
                      </Row>
                    ))}
                  </div>
                </Col>

                <Col md={12} className="text-end">
                  <div className="fw-semibold">Subtotal: {comprobanteForm.moneda} {fmt(subtotalComprobanteCalculado)}</div>
                  <div className="fw-semibold">IGV (18%): {comprobanteForm.moneda} {fmt(igvComprobanteCalculado)}</div>
                  <div className="fw-bold">Total: {comprobanteForm.moneda} {fmt(totalComprobanteCalculado)}</div>
                </Col>
              </Row>
               <div className="d-flex justify-content-end mt-3 gap-2">
                  {editandoId && <Button variant="secondary" onClick={() => { setEditandoId(null); setComprobanteForm(COMPROBANTE_INICIAL); }}>Cancelar Edición</Button>}
                  <Button type="submit" disabled={saving || !rucEmpresa}>{saving ? "Guardando..." : (editandoId ? "Actualizar Comprobante" : "Registrar Comprobante")}</Button>
               </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white fw-semibold">
          {seccion === "ordenes" ? "Órdenes de Venta" : "Comprobantes de Venta"} registrados
        </Card.Header>
        <Card.Body className="p-0">
          <div style={{ maxHeight: "600px", overflowY: "auto" }}>
            <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                {seccion === "ordenes" ? (
                  <>
                    <th>Fecha</th><th>Cliente</th><th>Documento</th><th className="text-end">Total</th><th className="text-end">Facturado</th><th className="text-end">Saldo</th><th className="text-center">Estado</th><th className="text-center">Acciones</th>
                  </>
                ) : (
                  <>
                    <th>Fecha</th><th>Documento</th><th>Cliente</th><th>Asiento</th><th className="text-end">Total</th><th className="text-center">Acciones</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={seccion === "ordenes" ? 8 : 6} className="text-center py-3 text-muted">Cargando...</td></tr>
              ) : seccion === "ordenes" ? (
                ordenes.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-3 text-muted">Sin registros.</td></tr>
                ) : (
                  ordenes.slice(0, 100).map((o) => (
                    (() => {
                      const total = toNum(o.total);
                      const facturado = toNum(o.total_facturado);
                      const saldo = toNum(o.saldo_pendiente != null ? o.saldo_pendiente : (total - facturado));
                      const estadoFacturacion = String(o.estado_facturacion || (saldo <= 0.01 ? "FACTURADO" : (facturado > 0 ? "PARCIAL" : "PENDIENTE"))).toUpperCase();

                      return (
                    <tr key={o.id}>
                      <td>{o.fecha || "-"}</td>
                      <td>
                        <div>{o.cliente_razon_social || "-"}</div>
                        <small className="text-muted">{o.cliente_ruc || "-"}</small>
                      </td>
                      <td><span className="small text-success fw-semibold">{o.correlativo || `OS-${o.id.slice(0, 8)}`}</span></td>
                      <td className="text-end">{o.moneda || "PEN"} {fmt(total)}</td>
                      <td className="text-end">{o.moneda || "PEN"} {fmt(facturado)}</td>
                      <td className="text-end">{o.moneda || "PEN"} {fmt(saldo)}</td>
                      <td className="text-center">
                        <Badge bg={estadoFacturacion === "FACTURADO" ? "success" : (estadoFacturacion === "PARCIAL" ? "warning" : "secondary")}>
                          {estadoFacturacion}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                            <Button variant="link" className="text-dark p-1" title="Descargar PDF" onClick={() => imprimirOV(o)}>
                            <FaPrint size={14} />
                            </Button>
                            <Button variant="link" className="text-primary p-1" title="Editar" onClick={() => handleEditOrden(o)} disabled={facturado > 0}>
                            <FaEdit size={14} />
                            </Button>
                            <Button variant="link" className="text-danger p-1" title="Eliminar" onClick={() => handleDeleteOrden(o.id)} disabled={facturado > 0}>
                            <FaTrash size={14} />
                            </Button>
                        </div>
                      </td>
                    </tr>
                      );
                    })()
                  ))
                )
              ) : comprobantes.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-3 text-muted">Sin registros.</td></tr>
              ) : (
                comprobantes.slice(0, 100).map((c) => (
                  <tr key={c.id}>
                    <td>{c.fecha_emision || "-"}</td>
                    <td>{c.tipo} {c.serie}-{c.numero}</td>
                    <td>{c.cliente_razon_social || "-"}</td>
                    <td>{c.asiento_id ? <span className="small text-success fw-semibold">{c.asiento_id}</span> : <span className="small text-muted">PENDIENTE</span>}</td>
                    <td className="text-end">
                      <div>{c.moneda || "PEN"} {fmt(c.total)}</div>
                      <small className={String(c.estado_pago || "PENDIENTE").toUpperCase() === "PAGADO" ? "text-success" : "text-warning"}>
                        Pago: {String(c.estado_pago || "PENDIENTE").toUpperCase()}
                      </small>
                    </td>
                    <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                            <Button variant="link" className="text-primary p-1" title="Editar" onClick={() => handleEditComprobante(c)}>
                                <FaEdit size={14} />
                            </Button>
                            <Button variant="link" className="text-danger p-1" title="Eliminar" onClick={() => handleDeleteComprobante(c.id)}>
                                <FaTrash size={14} />
                            </Button>
                        </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          </div>
        </Card.Body>
      </Card>
        </>
      )}

    </Container>
  );
};

export default GestionVentasModulo;
